import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { isAuthenticated } from '../lib/authUtils';
import { fetchCart, addCartItem, reduceCartItem, deleteCartItem } from '../services/cartService';
import { fetchProductById } from '../services/productService';
import { tierPrice } from '../lib/format';

const CartContext = createContext();
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
};

const read = (k, d) => { try { const v = JSON.parse(window.localStorage.getItem(k)); return v ?? d; } catch (e) { return d; } };

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => read('cart-items', []));
  const [catalog, setCatalog] = useState({});
  const [loading, setLoading] = useState(false);
  const [wishlist, setWishlist] = useState(() => read('wishlist', []));
  const [compareList, setCompareList] = useState(() => read('compareList', []));

  // Only the products that are actually in the cart are fetched (not the whole catalogue).
  const catalogRef = useRef({});
  useEffect(() => { catalogRef.current = catalog; }, [catalog]);
  const loadMissing = useCallback(async (ids) => {
    const missing = ids.map(String).filter((id) => !catalogRef.current[id]);
    if (!missing.length) return;
    const found = await Promise.all(missing.map((id) => fetchProductById(id).catch(() => null)));
    const add = Object.fromEntries(found.filter(Boolean).map((p) => [String(p.id), p]));
    if (Object.keys(add).length) setCatalog((prev) => ({ ...prev, ...add }));
  }, []);

  const loadCart = useCallback(async () => {
    if (!isAuthenticated()) { setItems(read('cart-items', [])); return; }
    try { setLoading(true); const server = await fetchCart(); setItems(server.map((c) => ({ id: c.id, quantity: c.quantity }))); } catch (e) { /* keep local */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCart(); }, [loadCart]);
  useEffect(() => {
    const h = () => { setCatalog({}); catalogRef.current = {}; loadCart(); };   // prices depend on who is signed in
    window.addEventListener('authStateChanged', h);
    return () => window.removeEventListener('authStateChanged', h);
  }, [loadCart]);
  const itemKey = items.map((i) => i.id).join(',');
  useEffect(() => { if (itemKey) loadMissing(itemKey.split(',')); }, [itemKey, catalog, loadMissing]);

  useEffect(() => { window.localStorage.setItem('cart-items', JSON.stringify(items)); }, [items]);
  useEffect(() => { window.localStorage.setItem('wishlist', JSON.stringify(wishlist)); }, [wishlist]);
  useEffect(() => { window.localStorage.setItem('compareList', JSON.stringify(compareList)); }, [compareList]);

  // cart lines = stored ids/quantities joined with the live catalogue (so account pricing and quantity breaks always apply)
  const cart = useMemo(() => items.map((it) => {
    const p = catalog[String(it.id)];
    if (!p) return null;
    const unit = tierPrice({ tiers: p.tiers, basePrice: p.price }, it.quantity);
    return { id: p.id, cartId: p.id, sku: p.sku, name: p.name, slug: p.slug, image: p.image, brand: p.brand, category: p.categoryName, stock: p.stock, quantity: it.quantity, price: unit, basePrice: p.price, listPrice: p.listPrice, tiers: p.tiers, lineTotal: unit * it.quantity, priceSource: p.priceSource };
  }).filter(Boolean), [items, catalog]);

  const addToCart = async (product, quantity = 1) => {
    setItems((prev) => (prev.find((i) => String(i.id) === String(product.id)) ? prev.map((i) => (String(i.id) === String(product.id) ? { ...i, quantity: i.quantity + quantity } : i)) : [...prev, { id: product.id, quantity }]));
    if (isAuthenticated()) { try { await addCartItem({ productId: product.id, productPrice: product.price, quantity }); } catch (e) { /* local state already updated */ } }
  };
  const removeFromCart = async (productId) => {
    setItems((prev) => prev.filter((i) => String(i.id) !== String(productId)));
    if (isAuthenticated()) { try { await deleteCartItem(productId); } catch (e) { /* ignore */ } }
  };
  const updateQuantity = async (productId, qty) => {
    if (qty <= 0) { removeFromCart(productId); return; }
    const cur = items.find((i) => String(i.id) === String(productId)); const diff = qty - (cur ? cur.quantity : 0);
    setItems((prev) => prev.map((i) => (String(i.id) === String(productId) ? { ...i, quantity: qty } : i)));
    if (isAuthenticated() && diff !== 0) { try { if (diff > 0) await addCartItem({ productId, productPrice: 0, quantity: diff }); else await reduceCartItem({ productId, productPrice: 0, quantity: -diff }); } catch (e) { /* ignore */ } }
  };
  const clearCart = async () => { const ids = items.map((i) => i.id); setItems([]); if (isAuthenticated()) await Promise.all(ids.map((id) => deleteCartItem(id).catch(() => null))); };
  const refreshCart = async () => { await loadCart(); };
  const getCartTotal = () => cart.reduce((t, i) => t + i.lineTotal, 0);
  const getCartCount = () => cart.reduce((n, i) => n + i.quantity, 0);
  const getCartSavings = () => cart.reduce((t, i) => t + Math.max((i.listPrice - i.price) * i.quantity, 0), 0);

  const addToWishlist = (p) => setWishlist((w) => (w.find((x) => x.id === p.id) ? w : [...w, p]));
  const removeFromWishlist = (id) => setWishlist((w) => w.filter((x) => x.id !== id));
  const isInWishlist = (id) => wishlist.some((x) => x.id === id);
  const addToCompare = (p) => setCompareList((c) => (c.find((x) => x.id === p.id) || c.length >= 4 ? c : [...c, p]));
  const removeFromCompare = (id) => setCompareList((c) => c.filter((x) => x.id !== id));
  const clearCompare = () => setCompareList([]);
  const isInCompare = (id) => compareList.some((x) => x.id === id);

  const value = { cart, loading, catalog, addToCart, removeFromCart, updateQuantity, clearCart, refreshCart, getCartTotal, getCartCount, getCartSavings, wishlist, addToWishlist, removeFromWishlist, isInWishlist, compareList, addToCompare, removeFromCompare, clearCompare, isInCompare };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
